import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShoppingCart, FiChevronLeft, FiChevronRight, FiAward, FiStar, FiExternalLink, FiImage, FiX } from "react-icons/fi";
import { FaCrown } from "react-icons/fa";
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import toast from 'react-hot-toast';

const HomeScreen = ({ theme = 'dark' }) => {
    const navigate = useNavigate();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [direction, setDirection] = useState(1);

    const [winningUsers, setWinningUsers] = useState([]);
    const [balloons, setBalloons] = useState([]);

    const [sampleNews, setSampleNews] = useState([]);      // full API list
    const [currentNews, setCurrentNews] = useState([]);    // 5 shown in UI
    const [nextNewsIndex, setNextNewsIndex] = useState(0);

    const [imageLoaders, setImageLoaders] = useState({});
    const [showNewsModal, setShowNewsModal] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);

    const [portfolioCards, setPortfolioCards] = useState([]);

    const isDark = theme === 'dark';

    const themeColors = useMemo(() => ({
        bodyBg: isDark ? '#020617' : '#f8f9fa',
        cardBg: isDark ? '#020617' : '#ffffff',
        rowBg: isDark ? 'rgba(15,23,42,0.92)' : '#ffffff',
        borderColor: isDark ? 'rgba(148,163,184,0.35)' : 'rgba(0,0,0,0.1)',
        textPrimary: isDark ? '#e5e7eb' : '#111827',
        textSecondary: isDark ? '#9ca3af' : '#6b7280',
    }), [isDark]);

    const FetchSlides = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/company/slider`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });

            if (response?.data?.error === 'session expired') {
                localStorage.removeItem('a5srf4wer7w1cd');
                navigate('/login');
            } else {
                // Your API format: { data: [ ... ] }
                const data = response?.data?.data || [];
                setPortfolioCards(Array.isArray(data) ? data : []);
                setCurrentSlide(0);
            }

        } catch (error) {
            toast.error('Failed to fetch sliders', { position: 'bottom-center' });
        }
    };

    const handleSliderClick = (slide) => {
        if (!slide) return;

        const action = (slide.onclick || '').toString().toLowerCase();

        if (action === 'screen' && slide.screen_path) {
            navigate(slide.screen_path);
            return;
        }

        if (action === 'link' && slide.link_url) {
            window.open(slide.link_url, '_blank', 'noopener,noreferrer');
            return;
        }

        // else do nothing
    };



    const FetchNews = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/company/news`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });

            // If your backend ever changes shape, log once and adjust:
            // console.log("NEWS RAW:", response?.data);

            const data = response?.data?.data || [];
            setSampleNews(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error('Failed to fetch news', { position: 'bottom-center' });
        }
    };

    useEffect(() => {
        FetchSlides();
        FetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Function to generate random user data with only popular crypto assets
    const generateRandomUser = (id) => {
        const firstNames = ["Alex", "Sarah", "Mike", "Emma", "James", "Olivia", "Liam", "Noah", "Ava", "Mason", "Sophia", "Ethan", "Isabella", "Logan", "Mia", "Lucas", "Charlotte", "Benjamin", "Amelia", "Elijah"];
        const lastNames = ["Chen", "Johnson", "Rodriguez", "Wilson", "Brown", "Martin", "Anderson", "Davis", "Thompson", "Lee", "Taylor", "White", "Harris", "Clark", "Lewis", "Walker", "Hall", "Young", "King", "Wright"];
        const popularAssets = ["BTC", "ETH", "BNB", "XRP", "SOL", "ADA", "DOGE", "DOT", "MATIC", "SHIB"];
        const timeOptions = ["Just now", "1 min ago", "2 mins ago", "3 mins ago", "4 mins ago", "5 mins ago"];

        const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
        const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
        const asset = popularAssets[Math.floor(Math.random() * popularAssets.length)];
        const profit = (Math.floor(Math.random() * 15000) + 1000).toLocaleString();
        const time = timeOptions[Math.floor(Math.random() * timeOptions.length)];

        return {
            id: id || Date.now() + Math.random(),
            name: `${firstName} ${lastName}`,
            profit,
            asset,
            time
        };
    };

    // Helper function to get rank color based on position
    const getRankColor = (position) => {
        const colors = {
            4: ['#94a3b8', '#64748b'],
            5: ['#f59e0b', '#d97706'],
            6: ['#f97316', '#ea580c'],
            7: ['#6366f1', '#4f46e5'],
            8: ['#8b5cf6', '#7c3aed'],
            9: ['#ec4899', '#db2777'],
            10: ['#14b8a6', '#0d9488'],
        };
        return colors[position] || colors[4];
    };

    // Helper function to format numbers in compact form
    const formatCompactNumber = (numStr) => {
        const num = parseInt(String(numStr).replace(/,/g, ''), 10);
        if (Number.isNaN(num)) return numStr;

        if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
        return numStr;
    };

    const topTraders = [
        { id: 1, name: "Crypto King", profit: "87,46,532", avatar: "CK", wins: 12790 },
        { id: 2, name: "Bull Master", profit: "75,87,211", avatar: "BM", wins: 10989 },
        { id: 3, name: "Wolf Trader", profit: "66,98,090", avatar: "WT", wins: 9898 },
        { id: 4, name: "Quantum Trader", profit: "55,12,456", avatar: "QT", wins: 8567 },
        { id: 5, name: "Phoenix Rider", profit: "48,95,321", avatar: "PR", wins: 7890 },
        { id: 6, name: "Oracle Eye", profit: "42,33,678", avatar: "OE", wins: 7123 },
        { id: 7, name: "Neptune Wave", profit: "39,87,543", avatar: "NW", wins: 6890 },
        { id: 8, name: "Alpha Hunter", profit: "36,54,321", avatar: "AH", wins: 6345 },
        { id: 9, name: "Beta Warrior", profit: "33,21,987", avatar: "BW", wins: 5987 },
        { id: 10, name: "Gamma Genius", profit: "29,87,654", avatar: "GG", wins: 5432 }
    ];

    const generateInitialWinningUsers = () => (
        Array.from({ length: 5 }, (_, i) => generateRandomUser(i + 1))
    );

    const createBalloons = () => {
        const newBalloons = [];
        for (let i = 0; i < 20; i++) {
            newBalloons.push({
                id: i,
                x: Math.random() * 100,
                delay: Math.random() * 5,
                color: `hsl(${Math.random() * 360}, 70%, 60%)`,
                size: 20 + Math.random() * 20,
                opacity: 0.2 + Math.random() * 0.3,
                shape: Math.random() > 0.7 ? 'oval' : 'round'
            });
        }
        setBalloons(newBalloons);
    };

    // Init winners + balloons once
    useEffect(() => {
        setWinningUsers(generateInitialWinningUsers());
        createBalloons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * IMPORTANT FIX #1:
     * When sampleNews arrives, initialize currentNews.
     */
    useEffect(() => {
        if (!sampleNews || sampleNews.length === 0) return;

        const firstBatch = sampleNews.slice(0, 5);
        setCurrentNews(firstBatch);
        setNextNewsIndex(Math.min(5, sampleNews.length));

        // Pre-mark thumbnails as "loading" so the loader shows.
        const loaders = {};
        firstBatch.forEach(n => { loaders[n.id] = true; });
        setImageLoaders(prev => ({ ...prev, ...loaders }));
    }, [sampleNews]);

    /**
     * IMPORTANT FIX #2:
     * Rotation interval must depend on sampleNews (fresh closure), and guard empty arrays.
     */
    useEffect(() => {
        if (!sampleNews || sampleNews.length === 0) return;

        const newsInterval = setInterval(() => {
            setCurrentNews(prev => {
                const safePrev = Array.isArray(prev) ? prev : [];
                if (safePrev.length === 0) {
                    const first = sampleNews.slice(0, 5);
                    return first;
                }

                const idx = (nextNewsIndex % sampleNews.length);
                const newItem = sampleNews[idx];

                // rotate: remove first, add new at end
                const updated = [...safePrev.slice(1), newItem];

                // mark new image as loading (so loader appears until onLoad/onError)
                setImageLoaders(loadPrev => ({
                    ...loadPrev,
                    [newItem?.id]: true
                }));

                return updated;
            });

            setNextNewsIndex(i => (i + 1) % sampleNews.length);
        }, 8000);

        return () => clearInterval(newsInterval);
    }, [sampleNews, nextNewsIndex]);

    // Handle image load/error
    const handleImageLoad = (newsId) => {
        setImageLoaders(prev => ({ ...prev, [newsId]: false }));
    };

    const handleImageError = (newsId) => {
        setImageLoaders(prev => ({ ...prev, [newsId]: false }));
    };

    const handleNewsClick = (news) => {
        setSelectedNews(news);
        setShowNewsModal(true);
    };

    const handleOpenExternalLink = () => {
        if (selectedNews?.url) {
            window.open(selectedNews.url, '_blank', 'noopener,noreferrer');
        }
        setShowNewsModal(false);
    };

    // Auto-rotate carousel (guard empty)
    useEffect(() => {
        if (!portfolioCards || portfolioCards.length === 0) return;

        const interval = setInterval(() => {
            setDirection(1);
            setCurrentSlide(prev => (prev + 1) % portfolioCards.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [portfolioCards.length]);

    // Add new winning users to the list (max 5)
    useEffect(() => {
        const interval = setInterval(() => {
            const newUser = generateRandomUser();
            setWinningUsers(prev => {
                const newUsers = [{ ...newUser, time: 'Just now' }, ...(prev || [])];
                return newUsers.slice(0, 5);
            });
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    const nextSlide = () => {
        if (!portfolioCards.length) return;
        setDirection(1);
        setCurrentSlide(prev => (prev + 1) % portfolioCards.length);
    };

    const prevSlide = () => {
        if (!portfolioCards.length) return;
        setDirection(-1);
        setCurrentSlide(prev => (prev - 1 + portfolioCards.length) % portfolioCards.length);
    };

    const slideVariants = {
        enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
        center: { zIndex: 1, x: 0, opacity: 1 },
        exit: (dir) => ({ zIndex: 0, x: dir < 0 ? 300 : -300, opacity: 0 })
    };

    const newsItemVariants = {
        enter: { opacity: 0, y: 80, height: 0, scale: 0.9 },
        center: {
            opacity: 1,
            y: 0,
            height: "auto",
            scale: 1,
            transition: {
                opacity: { duration: 0.6, ease: "easeOut" },
                y: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
                height: { duration: 0.7, ease: "easeOut" },
                scale: { duration: 0.5, ease: "easeOut" }
            }
        },
        exit: {
            opacity: 0,
            y: -50,
            height: 0,
            scale: 0.95,
            transition: {
                opacity: { duration: 0.4, ease: "easeIn" },
                y: { duration: 0.6, ease: "easeIn" },
                height: { duration: 0.5, ease: "easeIn" },
                scale: { duration: 0.3, ease: "easeIn" }
            }
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { type: "spring", damping: 25, stiffness: 300 }
        },
        exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-100"
            style={{ color: themeColors.textPrimary }}
        >
            {/* Portfolio Carousel */}
            <motion.div
                className="card border-0 shadow-sm mb-3 position-relative overflow-hidden"
                style={{ height: '200px', borderRadius: '15px', backgroundColor: themeColors.cardBg }}
                whileHover={{ scale: 1.02 }}
            >
                {/* If no slides, show a placeholder instead of breaking */}
                {portfolioCards.length === 0 ? (
                    <div className="w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ color: themeColors.textSecondary }}>
                        Loading banners...
                    </div>
                ) : (
                    <>
                        <div className="position-absolute w-100 h-100">
                            <AnimatePresence initial={false} custom={direction}>
                                <motion.div
                                    key={currentSlide}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.5 }}
                                    className="position-absolute w-100 h-100"
                                    onClick={() => handleSliderClick(portfolioCards[currentSlide])}
                                    style={{
                                        backgroundImage: `url(${portfolioCards[currentSlide]?.image_url})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        cursor: ['link', 'screen'].includes((portfolioCards[currentSlide]?.onclick || '').toLowerCase())
                                            ? 'pointer'
                                            : 'default'
                                    }}
                                />

                            </AnimatePresence>
                        </div>

                        {/* Controls */}
                        <button
                            className="btn btn-sm position-absolute start-0 top-50 translate-middle-y text-white"
                            onClick={prevSlide}
                            style={{
                                zIndex: 2,
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FiChevronLeft size={14} />
                        </button>

                        <button
                            className="btn btn-sm position-absolute end-0 top-50 translate-middle-y text-white"
                            onClick={nextSlide}
                            style={{
                                zIndex: 2,
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FiChevronRight size={14} />
                        </button>

                        {/* Indicators */}
                        <div className="position-absolute bottom-0 start-50 translate-middle-x mb-2" style={{ zIndex: 2 }}>
                            <div className="d-flex">
                                {portfolioCards.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`btn p-1 ${index === currentSlide ? 'text-white' : 'text-white-50'}`}
                                        onClick={() => {
                                            setDirection(index > currentSlide ? 1 : -1);
                                            setCurrentSlide(index);
                                        }}
                                        style={{ fontSize: '12px', lineHeight: '0.5' }}
                                    >
                                        •
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </motion.div>

            {/* Live Winners Section */}
            <motion.div
                className="card border-0 shadow-sm mb-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                style={{ borderRadius: '15px' }}
            >
                <div
                    className="card-header bg-transparent border-0 py-2"
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '15px 15px 0 0'
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 1, repeat: Infinity, repeatDelay: 5 }}>
                                <FiAward className="text-warning me-2" size={16} />
                            </motion.div>
                            <h6 className="fw-bold mb-0 text-white" style={{ fontSize: '14px' }}>Live Winners</h6>
                        </div>
                        <motion.div animate={{ opacity: [1, 0.7, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                            <div className="badge bg-warning text-dark" style={{ fontSize: '9px', padding: '3px 8px' }}>LIVE</div>
                        </motion.div>
                    </div>
                </div>

                <div className="card-body p-0" style={{ backgroundColor: themeColors.cardBg }}>
                    <div style={{ height: '220px', overflow: 'hidden', borderRadius: '0 0 15px 15px' }}>
                        <AnimatePresence mode="popLayout">
                            {winningUsers.map((user, index) => (
                                <motion.div
                                    key={user.id}
                                    layout
                                    initial={{ opacity: 0, x: -50, height: 0 }}
                                    animate={{ opacity: 1, x: 0, height: 'auto' }}
                                    exit={{ opacity: 0, x: 50, height: 0 }}
                                    transition={{ duration: 0.4, layout: { duration: 0.3 } }}
                                    className="border-bottom"
                                    style={{ borderColor: themeColors.borderColor }}
                                >
                                    <div
                                        className="p-2"
                                        style={{
                                            background: index === 0
                                                ? 'linear-gradient(135deg, #FFF9C4 0%, #FFEB3B 50%, #FFC107 100%)'
                                                : themeColors.rowBg,
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {index === 0 && (
                                            <motion.div
                                                className="position-absolute"
                                                style={{
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                                }}
                                                animate={{ left: ['-100%', '100%'] }}
                                                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
                                            />
                                        )}

                                        <div className="d-flex justify-content-between align-items-center position-relative">
                                            <div className="d-flex align-items-center">
                                                <div className="position-relative me-2">
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            fontSize: '10px',
                                                            background: index === 0
                                                                ? 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)'
                                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {user.name.split(' ').map(n => n[0]).join('')}
                                                    </div>
                                                    {index === 0 && (
                                                        <motion.div
                                                            initial={{ scale: 0, rotate: -180 }}
                                                            animate={{ scale: 1, rotate: 0 }}
                                                            transition={{ type: "spring", stiffness: 200 }}
                                                            className="position-absolute top-0 start-100 translate-middle"
                                                            style={{ fontSize: '8px' }}
                                                        >
                                                            👑
                                                        </motion.div>
                                                    )}
                                                </div>

                                                <div>
                                                    <h6
                                                        className="fw-bold mb-0"
                                                        style={{
                                                            fontSize: '12px',
                                                            color: index === 0 ? (isDark ? '#b45309' : '#D4AF37') : themeColors.textPrimary
                                                        }}
                                                    >
                                                        {user.name}
                                                    </h6>
                                                    <small className="d-flex align-items-center" style={{ fontSize: '9px', color: themeColors.textSecondary }}>
                                                        <FiStar size={8} className="text-warning me-1" />
                                                        {user.asset} • {user.time}
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="text-end">
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.2, type: "spring" }}
                                                    className="badge"
                                                    style={{
                                                        fontSize: '10px',
                                                        padding: '4px 8px',
                                                        background: index === 0
                                                            ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                                                            : 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                                                        color: 'white',
                                                        borderRadius: '8px',
                                                        boxShadow: index === 0 ? '0 2px 4px rgba(76, 175, 80, 0.3)' : 'none'
                                                    }}
                                                >
                                                    +{user.profit}
                                                </motion.div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>

            {/* Top Traders of All Time Section (unchanged UI) */}
            <motion.div
                className="card border-0 shadow-sm mb-3 position-relative overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                style={{
                    borderRadius: '15px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${themeColors.borderColor}`
                }}
            >
                {/* Enhanced Background Celebration Effects */}
                <div className="position-absolute w-100 h-100" style={{
                    background: isDark
                        ? 'radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.15) 0%, transparent 50%)'
                        : 'radial-gradient(circle at 30% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%)',
                    zIndex: 0
                }} />

                {/* Gold Dust Particle Effect */}
                <div className="position-absolute w-100 h-100" style={{ pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
                    {[...Array(25)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="position-absolute rounded-circle"
                            style={{
                                width: `${2 + Math.random() * 3}px`,
                                height: `${2 + Math.random() * 3}px`,
                                background: 'radial-gradient(circle, #FFD700 30%, transparent 70%)',
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                opacity: 0.1 + Math.random() * 0.2
                            }}
                            animate={{
                                y: [0, -10, 0],
                                x: [0, Math.sin(i) * 5, 0],
                                opacity: [0.1, 0.4, 0.1]
                            }}
                            transition={{
                                duration: 3 + Math.random() * 2,
                                repeat: Infinity,
                                delay: i * 0.1
                            }}
                        />
                    ))}
                </div>

                {/* Animated Floating Trophies */}
                <div className="position-absolute w-100 h-100" style={{ pointerEvents: 'none', zIndex: 0 }}>
                    {[...Array(8)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="position-absolute"
                            style={{
                                fontSize: '18px',
                                opacity: 0.07 + Math.random() * 0.08,
                                left: `${10 + (i * 10)}%`,
                                top: `${20 + Math.random() * 60}%`
                            }}
                            animate={{
                                y: [0, -20, 0],
                                rotate: [0, 5, -5, 0]
                            }}
                            transition={{
                                duration: 4 + Math.random() * 3,
                                repeat: Infinity,
                                delay: i * 0.5
                            }}
                        >
                            🏆
                        </motion.div>
                    ))}
                </div>

                {/* Header with Enhanced Styling */}
                <div
                    className="card-header bg-transparent border-0 py-3 position-relative"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '15px 15px 0 0',
                        zIndex: 2,
                        borderBottom: `1px solid ${themeColors.borderColor}`
                    }}
                >
                    <div className="d-flex align-items-center justify-content-center position-relative">
                        <motion.div
                            className="position-absolute"
                            style={{
                                width: '60px',
                                height: '60px',
                                background: 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)',
                                borderRadius: '50%',
                                filter: 'blur(8px)'
                            }}
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.5, 0.3]
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity
                            }}
                        />
                        <motion.div
                            animate={{
                                rotate: [0, 15, -15, 0],
                                scale: [1, 1.1, 1]
                            }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
                            style={{ zIndex: 1 }}
                        >
                            <FaCrown className="me-2" style={{
                                color: '#FFD700',
                                filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 0.5))'
                            }} size={20} />
                        </motion.div>
                        <h6 className="fw-bold mb-0" style={{
                            fontSize: '15px',
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            zIndex: 1
                        }}>
                            Top Traders of All Time
                        </h6>
                    </div>
                </div>

                {/* Top 3 Traders Podium */}
                <div className="card-body p-3 pt-4 position-relative" style={{ zIndex: 2 }}>
                    <div className="row align-items-end justify-content-center text-center mb-4">
                        {/* 2nd Place */}
                        <div className="col-4">
                            <motion.div
                                className="text-center"
                                initial={{ scale: 0.8, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                transition={{ delay: 0.8, type: "spring" }}
                            >
                                <motion.div
                                    className="position-relative d-inline-block mb-2"
                                    animate={{ y: [0, -3, 0] }}
                                    transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto position-relative"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            background: 'linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%)',
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            border: '3px solid #D4D4D4',
                                            boxShadow: '0 4px 8px rgba(192, 192, 192, 0.3)'
                                        }}
                                    >
                                        {topTraders[1].avatar}
                                        <div className="position-absolute top-0 start-100 translate-middle"
                                            style={{ transform: 'translate(-50%, -50%)' }}>
                                            <div className="badge bg-secondary text-white rounded-pill px-2 py-1"
                                                style={{ fontSize: '9px', minWidth: '28px' }}>
                                                2nd
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                                <h6 className="fw-bold mb-1" style={{ fontSize: '12px', color: themeColors.textPrimary }}>
                                    {topTraders[1].name}
                                </h6>
                                <div className="badge bg-success text-white mb-1 px-2 py-1" style={{ fontSize: '10px' }}>
                                    +{topTraders[1].profit}
                                </div>
                                <small className="d-block" style={{ fontSize: '9px', color: themeColors.textSecondary }}>
                                    {topTraders[1].wins} Wins
                                </small>
                            </motion.div>
                        </div>

                        {/* 1st Place */}
                        <div className="col-4">
                            <motion.div
                                className="text-center"
                                initial={{ scale: 0.8, y: -20 }}
                                animate={{ scale: 1.1, y: 0 }}
                                transition={{ delay: 0.7, type: "spring" }}
                            >
                                <motion.div
                                    className="position-relative d-inline-block mb-2"
                                    animate={{ y: [0, -8, 0] }}
                                    transition={{ duration: 2.5, repeat: Infinity }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto position-relative"
                                        style={{
                                            width: '70px',
                                            height: '70px',
                                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                            color: 'white',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            border: '4px solid #FFC107',
                                            boxShadow: '0 6px 12px rgba(255, 215, 0, 0.4)',
                                            zIndex: 3
                                        }}
                                    >
                                        {topTraders[0].avatar}
                                        <motion.div
                                            className="position-absolute top-0 start-50 translate-middle"
                                            style={{ transform: 'translate(-50%, -50%)' }}
                                            animate={{ rotate: [0, 360] }}
                                            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                                        >
                                            <FaCrown size={14} className="text-white" />
                                        </motion.div>
                                    </div>
                                    <div className="position-absolute top-0 start-100 translate-middle"
                                        style={{ transform: 'translate(-30%, -50%)' }}>
                                        <div className="badge bg-warning text-dark rounded-pill px-2 py-1"
                                            style={{ fontSize: '9px', minWidth: '28px' }}>
                                            1st
                                        </div>
                                    </div>
                                </motion.div>
                                <h6 className="fw-bold mb-1" style={{ fontSize: '13px', color: themeColors.textPrimary }}>
                                    {topTraders[0].name}
                                </h6>
                                <div className="badge bg-success text-white mb-1 px-2 py-1" style={{ fontSize: '11px' }}>
                                    +{topTraders[0].profit}
                                </div>
                                <small className="d-block" style={{ fontSize: '9px', color: themeColors.textSecondary }}>
                                    {topTraders[0].wins} Wins
                                </small>
                            </motion.div>
                        </div>

                        {/* 3rd Place */}
                        <div className="col-4">
                            <motion.div
                                className="text-center"
                                initial={{ scale: 0.8, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                transition={{ delay: 0.9, type: "spring" }}
                            >
                                <motion.div
                                    className="position-relative d-inline-block mb-2"
                                    animate={{ y: [0, -2, 0] }}
                                    transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto position-relative"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            background: 'linear-gradient(135deg, #CD7F32 0%, #A57128 100%)',
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            border: '3px solid #B08D57',
                                            boxShadow: '0 4px 8px rgba(205, 127, 50, 0.3)'
                                        }}
                                    >
                                        {topTraders[2].avatar}
                                        <div className="position-absolute top-0 start-100 translate-middle"
                                            style={{ transform: 'translate(-50%, -50%)' }}>
                                            <div className="badge bg-danger text-white rounded-pill px-2 py-1"
                                                style={{ fontSize: '9px', minWidth: '28px' }}>
                                                3rd
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                                <h6 className="fw-bold mb-1" style={{ fontSize: '12px', color: themeColors.textPrimary }}>
                                    {topTraders[2].name}
                                </h6>
                                <div className="badge bg-success text-white mb-1 px-2 py-1" style={{ fontSize: '10px' }}>
                                    +{topTraders[2].profit}
                                </div>
                                <small className="d-block" style={{ fontSize: '9px', color: themeColors.textSecondary }}>
                                    {topTraders[2].wins} Wins
                                </small>
                            </motion.div>
                        </div>
                    </div>

                    {/* 4th to 10th Top Traders - TABLE LIST VIEW */}
                    <motion.div
                        className="mt-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        <div className="rounded overflow-hidden" style={{
                            border: `1px solid ${themeColors.borderColor}`,
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.3)' : 'rgba(243, 244, 246, 0.3)'
                        }}>
                            <div className="table-responsive">
                                <table className="table table-borderless mb-0" style={{ fontSize: '11px' }}>
                                    <thead>
                                        <tr style={{
                                            background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(229, 231, 235, 0.5)',
                                            color: themeColors.textSecondary,
                                            fontWeight: '500'
                                        }}>
                                            <th className="py-2 ps-3" style={{ width: '15%', fontSize: '10px' }}>Rank</th>
                                            <th className="py-2" style={{ width: '45%', fontSize: '10px' }}>Trader</th>
                                            <th className="py-2" style={{ width: '20%', fontSize: '10px' }}>Wins</th>
                                            <th className="py-2 pe-3 text-end" style={{ width: '20%', fontSize: '10px' }}>Profit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topTraders.slice(3).map((trader, index) => (
                                            <motion.tr
                                                key={trader.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 1.1 + (index * 0.1) }}
                                                className="border-top"
                                                style={{
                                                    borderColor: themeColors.borderColor,
                                                    backgroundColor: index % 2 === 0
                                                        ? (isDark ? 'rgba(15, 23, 42, 0.1)' : 'rgba(255, 255, 255, 0.8)')
                                                        : 'transparent'
                                                }}
                                                whileHover={{
                                                    backgroundColor: isDark
                                                        ? 'rgba(30, 41, 59, 0.7)'
                                                        : 'rgba(243, 244, 246, 0.9)',
                                                    transition: { duration: 0.2 }
                                                }}
                                            >
                                                <td className="py-2 ps-3 align-middle">
                                                    <div className="d-flex align-items-center">
                                                        <div className="position-relative">
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    background: `linear-gradient(135deg, ${getRankColor(index + 4)})`,
                                                                    color: 'white',
                                                                    fontSize: '9px',
                                                                    fontWeight: 'bold',
                                                                    marginRight: '8px'
                                                                }}
                                                            >
                                                                {index + 4}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-2 align-middle">
                                                    <div className="d-flex align-items-center">
                                                        <div className="position-relative me-2">
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {trader.avatar}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold" style={{ fontSize: '11px', color: themeColors.textPrimary }}>
                                                                {trader.name}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-2 align-middle">
                                                    <div className="d-flex align-items-center">
                                                        <span style={{ fontSize: '10px', color: themeColors.textPrimary }}>
                                                            {trader.wins.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-2 pe-3 text-end align-middle">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div className="badge bg-success text-white py-1 px-2" style={{ fontSize: '10px' }}>
                                                            +{formatCompactNumber(trader.profit)}
                                                        </div>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>

                    {/* Celebration Text */}
                    <motion.div
                        className="text-center mt-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4 }}
                    >
                        <div className="d-inline-flex align-items-center justify-content-center px-3 py-1 rounded-pill"
                            style={{
                                background: isDark
                                    ? 'rgba(255, 215, 0, 0.1)'
                                    : 'rgba(255, 215, 0, 0.15)',
                                border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 215, 0, 0.4)'}`
                            }}>
                            <motion.span
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                style={{ fontSize: '12px', marginRight: '6px' }}
                            >
                                🎉
                            </motion.span>
                            <small style={{
                                fontSize: '10px',
                                color: isDark ? '#FBBF24' : '#D97706',
                                fontWeight: '500'
                            }}>
                                Legendary Traders Hall of Fame
                            </small>
                            <motion.span
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                                style={{ fontSize: '12px', marginLeft: '6px' }}
                            >
                                🏆
                            </motion.span>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Live News Section */}
            <motion.div
                className="card border-0 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                style={{ borderRadius: '15px', backgroundColor: themeColors.cardBg }}
            >
                <div
                    className="card-header bg-transparent border-0 py-2"
                    style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        borderRadius: '15px 15px 0 0'
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                                <div className="bg-white rounded-circle d-flex align-items-center justify-content-center me-2"
                                    style={{ width: '20px', height: '20px' }}>
                                    <span style={{ fontSize: '10px', color: '#059669', fontWeight: 'bold' }}>📰</span>
                                </div>
                            </motion.div>
                            <h6 className="fw-bold mb-0 text-white" style={{ fontSize: '14px' }}>Live Market News</h6>
                        </div>
                        <motion.div animate={{ opacity: [1, 0.7, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                            <div className="badge bg-white text-success" style={{ fontSize: '9px', padding: '3px 8px' }}>
                                LIVE
                            </div>
                        </motion.div>
                    </div>
                </div>

                <div className="card-body p-0" style={{ backgroundColor: themeColors.cardBg }}>
                    <div style={{ height: '280px', overflow: 'hidden', borderRadius: '0 0 15px 15px', position: 'relative' }}>
                        {(!currentNews || currentNews.length === 0) ? (
                            <div className="p-3" style={{ color: themeColors.textSecondary, fontSize: '12px' }}>
                                Loading news...
                            </div>
                        ) : (
                            <AnimatePresence mode="popLayout">
                                {currentNews.map((news) => (
                                    <motion.div
                                        key={news?.id}
                                        variants={newsItemVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        className="border-bottom"
                                        style={{
                                            borderColor: themeColors.borderColor,
                                            backgroundColor: themeColors.rowBg,
                                            cursor: 'pointer'
                                        }}
                                        whileHover={{
                                            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(243, 244, 246, 0.8)'
                                        }}
                                        onClick={() => handleNewsClick(news)}
                                    >
                                        <div className="p-2">
                                            <div className="d-flex align-items-start">
                                                {/* Thumbnail */}
                                                <div className="position-relative me-2 flex-shrink-0" style={{ width: '60px', height: '40px' }}>
                                                    {/* Loader (also shown when image url is null) */}
                                                    {(imageLoaders[news?.id] !== false || !news?.urlToImage) && (
                                                        <div
                                                            className="d-flex align-items-center justify-content-center rounded"
                                                            style={{
                                                                width: '60px',
                                                                height: '40px',
                                                                background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(243, 244, 246, 0.5)',
                                                                border: `1px solid ${themeColors.borderColor}`
                                                            }}
                                                        >
                                                            <motion.div
                                                                animate={{ rotate: 360 }}
                                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                            >
                                                                <FiImage size={16} color={themeColors.textSecondary} />
                                                            </motion.div>
                                                        </div>
                                                    )}

                                                    {/* Only render img if urlToImage exists */}
                                                    {news?.urlToImage && (
                                                        <img
                                                            src={news.urlToImage}
                                                            alt={news?.title}
                                                            className="rounded"
                                                            style={{
                                                                width: '60px',
                                                                height: '40px',
                                                                objectFit: 'cover',
                                                                display: imageLoaders[news?.id] === false ? 'block' : 'none'
                                                            }}
                                                            onLoad={() => handleImageLoad(news?.id)}
                                                            onError={() => handleImageError(news?.id)}
                                                        />
                                                    )}
                                                </div>

                                                <div className="flex-grow-1 d-flex align-items-center" style={{ minWidth: 0 }}>
                                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                                            <span
                                                                className="text-truncate"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    color: '#10b981',
                                                                    fontWeight: '500',
                                                                    maxWidth: '70%'
                                                                }}
                                                            >
                                                                {news?.author || 'Unknown'}
                                                            </span>

                                                            <small style={{ fontSize: '9px', color: themeColors.textSecondary, flexShrink: 0 }}>
                                                                {news?.publishedAt
                                                                    ? new Date(news.publishedAt).toLocaleString()
                                                                    : ''}
                                                            </small>
                                                        </div>

                                                        <h6
                                                            className="mb-0 text-truncate"
                                                            style={{ fontSize: '11px', lineHeight: '1.3', color: themeColors.textPrimary }}
                                                            title={news?.title}
                                                        >
                                                            {news?.title}
                                                        </h6>
                                                    </div>

                                                    <div className="ms-2 flex-shrink-0">
                                                        <FiExternalLink
                                                            size={12}
                                                            color={themeColors.textSecondary}
                                                            className="opacity-75"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* News Modal */}
            <AnimatePresence>
                {showNewsModal && selectedNews && (
                    <motion.div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="modal-dialog modal-dialog-centered"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div
                                className="modal-content border-0 shadow"
                                style={{ backgroundColor: themeColors.cardBg, borderRadius: '15px' }}
                            >
                                <div
                                    className="modal-header border-0 position-relative"
                                    style={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        borderRadius: '15px 15px 0 0',
                                        padding: '1rem'
                                    }}
                                >
                                    <h6 className="modal-title fw-bold text-white mb-0" style={{ fontSize: '14px' }}>
                                        Open External Link
                                    </h6>

                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={() => setShowNewsModal(false)}
                                        style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)' }}
                                    >
                                        <FiX size={16} />
                                    </button>
                                </div>

                                <div className="modal-body p-3" style={{ color: themeColors.textPrimary }}>
                                    <div className="text-center mb-3">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                color: 'white'
                                            }}
                                        >
                                            <FiExternalLink size={20} />
                                        </div>
                                        <h6 className="fw-bold mb-2" style={{ fontSize: '14px' }}>External Website</h6>
                                        <p className="mb-0" style={{ fontSize: '12px', color: themeColors.textSecondary }}>
                                            You are about to open an external website. Please make sure you trust the source before proceeding.
                                        </p>
                                    </div>

                                    <div
                                        className="p-2 rounded mb-3"
                                        style={{
                                            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(243, 244, 246, 0.5)',
                                            border: `1px solid ${themeColors.borderColor}`
                                        }}
                                    >
                                        <h6 className="fw-bold mb-1" style={{ fontSize: '12px' }}>
                                            {selectedNews.title}
                                        </h6>
                                        <small style={{ fontSize: '10px', color: themeColors.textSecondary }}>
                                            By {selectedNews.author || 'Unknown'}
                                        </small>
                                    </div>

                                    <small className="d-block text-center mb-3" style={{ fontSize: '10px', color: themeColors.textSecondary }}>
                                        URL: <span style={{ color: '#10b981' }}>{selectedNews.url}</span>
                                    </small>
                                </div>

                                <div className="modal-footer border-0 pt-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShowNewsModal(false)}
                                        style={{ fontSize: '12px', borderRadius: '8px', border: 'none', padding: '6px 12px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success btn-sm"
                                        onClick={handleOpenExternalLink}
                                        style={{
                                            fontSize: '12px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            padding: '6px 12px',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                        }}
                                    >
                                        Open Link
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default HomeScreen;
